/**
 * @typedef {import('./AdminContact').default} AdminContact
 * @typedef {import('../../../config/local/Import/Data').default<'contact'>} ImportData
 * @typedef {import('../../Agent/agent-service').Contact} Contact
 * @typedef {import('../../Agent/Contacts/ContactProps').Source} Source
 * @typedef {import('../Query/Parameters').default} QueryParameters
 * @typedef {import('./BulkResendsContactInvite').default} BulkResendsContactInvite
 * @typedef {import('./CreatesContact').default} CreatesContact
 * @typedef {import('./Dependencies').default} Dependencies
 * @typedef {import('./GetContactsResponse').default} GetContactsResponse
 * @typedef {import('./GetsContactCount').default} GetsContactCount
 * @typedef {import('./GetsContacts').default} GetsContacts
 * @typedef {import('./ImportsContacts').default} ImportsContacts
 * @typedef {import('./InvitesContact').default} InvitesContact
 * @typedef {import('./RemoveContactResponse').default} RemoveContactResponse
 * @typedef {import('./RemovesContact').default} RemovesContact
 */

import { mapContactToRequest } from '../../Agent/agent-service';
import { prepareQueryForUrl } from '../Query';
import apiUrls from '../../../config/local/api-urls';
import ServiceError from '../../../shared/Errors/ServiceError';
import utilities from '@mooveguru/js-utilities';

/**
 * @implements {BulkResendsContactInvite}
 * @implements {CreatesContact}
 * @implements {GetsContactCount}
 * @implements {GetsContacts}
 * @implements {ImportsContacts}
 * @implements {InvitesContact}
 * @implements {RemovesContact}
 */
export default class ContactService {
	/** @param {Dependencies} dependencies */
	constructor(dependencies) {
		this.authService = dependencies.authService;
		this.httpClient = dependencies.httpClient;
	}

	/**
	 * @returns {Promise<number>}
	 */
	async getContactCount() {
		const response = await this.httpClient.get(
			`${apiUrls.me.administrator.contacts}`,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
			})
		);

		if (!response.isOk) {
			throw new Error(response.body.message);
		}

		return response.body.results.total;
	}

	/**
	 * Query param can accept a page number so as to not break legacy calls
	 * @param {number | QueryParameters} [query]
	 * @returns {Promise<GetContactsResponse>}
	 */
	async getContacts(query = 1) {
		const params = prepareQueryForUrl(
			ContactService.#normalizeContactsQuery(query),
			true
		);

		const response = await this.httpClient.get(
			apiUrls.administrators.contacts + params,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
				'Content-Type': 'application/json',
			})
		);

		if (!response.isOk) {
			throw new Error(response.body.message);
		}

		return ContactService.#mapGetContactsResponse(response.body);
	}

	/**
	 * @param {number | QueryParameters} query
	 * @returns {QueryParameters}
	 */
	static #normalizeContactsQuery(query) {
		if (typeof query === 'number') {
			return { limit: 50, page: query };
		}

		if (query.limit) {
			return query;
		}

		return {
			...query,
			limit: 50,
		};
	}

	/**
	 * @param {any} body
	 * @returns {GetContactsResponse}
	 */
	static #mapGetContactsResponse(body) {
		const data = body.contacts?.map(ContactService.#mapContacts);

		return {
			data,
			enrolledContacts: body.enrolled_contacts ?? 0,
			pages: body.pages ?? 1,
			total: body.total ?? data?.length ?? 0,
		};
	}

	/**
	 * @param {any} contact
	 * @returns {AdminContact}
	 */
	static #mapContacts(contact) {
		return {
			acceptedInvite: contact?.accepted_invite,
			agent: ContactService.#mapAgent(contact?.agent),
			aiMonitoring: {
				billableThrough:
					contact?.ai_monitoring?.billable_through ?? null,
				status: contact?.ai_monitoring?.status ?? 'off',
			},
			deletedAt: contact?.deleted_at,
			email: contact?.email,
			hasSubscription: contact?.has_subscription || false,
			homeownerId: contact?.homeowner_id,
			id: contact.id,
			isOwner: contact?.is_owner,
			languagePreference: contact?.language_preference,
			latestActivityAt: contact?.latest_activity_at
				? utilities.date.convertDateToDateString(
						new Date(contact?.latest_activity_at)
				  )
				: null,
			latestActivityDescription:
				contact?.latest_activity_description ?? null,
			leadScore: contact?.lead_score,
			leadScores: contact?.lead_scores,
			name: contact?.name,
			phone: {
				landline: contact?.phone_numbers?.find(
					/**
					 * @param {{type: string}} phoneNumber
					 * @returns {boolean}
					 */
					(phoneNumber) =>
						['landline', 'home']?.includes(phoneNumber.type)
				)?.value,
				mobile: contact?.phone_numbers?.find(
					/**
					 * @param {{type:string}} phoneNumber
					 * @returns {boolean}
					 */
					(phoneNumber) => phoneNumber.type === 'mobile'
				)?.value,
			},
			salutation: contact?.agent?.salutation,
			source: contact?.source,
			subscribedEmail: contact?.subscribed_email,
			subscribedPhone: contact?.subscribed_phone,
			tagIds: contact?.tag_ids ?? [],
		};
	}

	/**
	 * @param {any} agent
	 * @returns {AdminContact['agent']}
	 */
	static #mapAgent(agent) {
		return {
			email: agent?.email,
			groups: agent?.groups.length
				? agent?.groups?.map(
						/**
						 * @param {{id?: string, is_primary?: boolean, name?: string}} group
						 */
						(group) => ({
							id: group?.id,
							isPrimary: group?.is_primary,
							name: group?.name,
						})
				  )
				: [],
			id: agent?.id,
			name: agent?.name,
			phone: {
				landline: agent?.phone.landline,
				mobile: agent?.phone.mobile,
			},
			slug: agent?.slug,
			socialMedia: {
				facebook: agent?.social_media.facebook,
				twitter: agent?.social_media.twitter,
			},
			website: agent?.website,
		};
	}

	/**
	 * @param {{data: ImportData, file: File}} importData
	 * @param {Source} source
	 * @param {{
	 * 	agentId?: string,
	 * 	sendInvites?: boolean,
	 * 	uploadType?: 'single' | 'multi'
	 * }} [options]
	 */
	async importContacts(importData, source, options = {}) {
		const { agentId, sendInvites = true, uploadType = 'single' } = options;

		const endpoint =
			agentId !== undefined
				? `${apiUrls.agents.root}/${agentId}/contacts/import/`
				: apiUrls.contacts.import;

		const formData = new FormData();

		formData.append('file', importData.file);

		const { invalidData: invalid, validData: valid } = importData.data;
		const data = {
			invalid,
			valid,
		};

		formData.append('contacts', JSON.stringify(data));
		formData.append('source', source);
		formData.append('upload_type', uploadType);

		const response = await this.httpClient.post(
			`${endpoint}?sendInvites=${sendInvites}`,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
			}),
			formData
		);

		if (!response.isOk) {
			throw new ServiceError(
				'Something went wrong while trying to import your contacts.',
				response.body
			);
		}
	}

	// TODO Resolve issues with creating contact - likely an API issue
	/**
	 * @param {string} accessToken
	 * @param {Contact} values
	 * @param {Source} source
	 * @param {boolean} [sendInvite]
	 * @returns {Promise<void>}
	 */
	async createContact(accessToken, values, source, sendInvite = true) {
		const response = await this.httpClient.post(
			`${apiUrls.contacts.add}?sendInvite=${sendInvite}`,
			new Headers({
				Authorization: `Bearer ${accessToken}`,
				'Content-Type': 'application/json',
			}),

			mapContactToRequest(values, source)
		);

		if (!response.isOk) {
			const error = /** @type {Error & {statusCode?: number}} */ (
				new Error(response.body)
			);
			error.statusCode = response.statusCode;
			throw error;
		}
	}

	// TODO Resolve issues with creating contact - likely an API issue
	/**
	 * @param {string} id
	 * @param {string} accessToken
	 * @param {Contact} data
	 */
	async updateContact(id, accessToken, data) {
		const response = await this.httpClient.put(
			`${apiUrls.contacts.update}/${id}`,
			new Headers({
				Authorization: `Bearer ${accessToken}`,
				'Content-Type': 'application/json',
			}),
			mapContactToRequest(data, data.source ?? 'Agent added')
		);

		if (!response.isOk) {
			const error = /** @type {Error & {statusCode?: number}} */ (
				new Error(response.body)
			);
			error.statusCode = response.statusCode;
			throw error;
		}
	}

	/**
	 * Invite contact by id
	 * @param {string} id
	 * @returns {Promise<void>}
	 */
	async inviteContact(id) {
		const response = await this.httpClient.post(
			`${apiUrls.contacts.invite}/${id}`,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
				'Content-Type': 'application/json',
			})
		);

		if (!response.isOk) {
			throw new Error(response.body.join(''));
		}
	}

	/**
	 * @param {string[]} idList
	 * @returns {Promise<void>}
	 */
	async bulkResendContactInvite(idList) {
		const response = await this.httpClient.post(
			apiUrls.contacts.inviteBulk,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
				'Content-Type': 'application/json',
			}),
			{ idList }
		);

		if (!response.isOk) {
			throw new Error(response.body.message);
		}
	}

	/**
	 * @param {string[]} idList
	 * @returns {Promise<{total: number, updated: number}>}
	 */
	async addContactsToAiMonitoring(idList) {
		const response = await this.httpClient.post(
			apiUrls.contacts.addAiMonitoring,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
				'Content-Type': 'application/json',
			}),
			{ idList }
		);

		if (!response.isOk) {
			throw new Error(response.body.message);
		}

		return {
			total: response.body.total ?? 0,
			updated: response.body.updated ?? 0,
		};
	}

	/**
	 * @param {string[]} idList
	 * @returns {Promise<import('./AiMonitoringPreviewResponse').default>}
	 */
	async previewAddContactsToAiMonitoring(idList) {
		const response = await this.httpClient.post(
			apiUrls.contacts.addAiMonitoringPreview,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
				'Content-Type': 'application/json',
			}),
			{ idList }
		);

		if (!response.isOk) {
			let errorMessage = response.body?.message;

			if (Array.isArray(response.body)) {
				errorMessage = response.body[0];
			} else if (typeof response.body === 'string') {
				errorMessage = response.body;
			}

			throw new ServiceError(
				errorMessage || 'Unable to load billing preview.',
				response.body
			);
		}

		return {
			contactsToAdd: response.body.contacts_to_add ?? 0,
			currentBillableContactCount:
				response.body.current_billable_contact_count ?? 0,
			currentMonthlyChargeInCents:
				response.body.current_monthly_charge_in_cents ?? 0,
			immediateChargeInCents:
				response.body.immediate_charge_in_cents ?? 0,
			newMonthlyChargeInCents:
				response.body.new_monthly_charge_in_cents ?? 0,
			projectedBillableContactCount:
				response.body.projected_billable_contact_count ?? 0,
		};
	}

	/**
	 * @param {string[]} data
	 * @returns {Promise<RemoveContactResponse>}
	 */
	async removeContact(data) {
		const response = await this.httpClient.post(
			apiUrls.contacts.remove,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
				'Content-Type': 'application/json',
			}),
			{ contactId: data }
		);

		if (!response.isOk) {
			throw new Error(response.body.message);
		}

		const results = response.body;

		return {
			deleted: results.deleted,
			deletedIds: results.deleted_ids,
			total: results.total,
		};
	}

	/**
	 * @param {string[]} idList
	 * @returns {Promise<{total: number, updated: number}>}
	 */
	async removeContactsFromAiMonitoring(idList) {
		const response = await this.httpClient.post(
			apiUrls.contacts.removeAiMonitoring,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
				'Content-Type': 'application/json',
			}),
			{ idList }
		);

		if (!response.isOk) {
			throw new Error(response.body.message);
		}

		return {
			total: response.body.total ?? 0,
			updated: response.body.updated ?? 0,
		};
	}
}
